import { CANVAS_ELEMENT_TYPES } from "./nativeCanvasElements";

/**
 * Canvas image elements persist a stable blob reference instead of base64
 * pixels: `netpad-blob:<blobName>`. Data URLs only exist transiently (fresh
 * paste, offline notes, or legacy scenes awaiting migration).
 */
export const CANVAS_IMAGE_REF_SCHEME = "netpad-blob:";
export const MAX_CANVAS_IMAGE_BYTES = 10 * 1024 * 1024;

const DATA_URL_PREFIX = "data:";
const BASE64_BYTES_PER_CHAR = 3 / 4;

/**
 * @param {unknown} src
 * @returns {boolean}
 */
export const isCanvasImageRef = (src) =>
  typeof src === "string" && src.startsWith(CANVAS_IMAGE_REF_SCHEME);

/**
 * @param {string} blobName
 * @returns {string}
 */
export const createCanvasImageRef = (blobName) =>
  `${CANVAS_IMAGE_REF_SCHEME}${blobName}`;

/**
 * @param {string} assetRef
 * @returns {string | null}
 */
export const getCanvasImageBlobName = (assetRef) =>
  isCanvasImageRef(assetRef)
    ? assetRef.slice(CANVAS_IMAGE_REF_SCHEME.length)
    : null;

/**
 * @param {unknown} src
 * @returns {boolean}
 */
export const isDataUrlSrc = (src) =>
  typeof src === "string" && src.startsWith(DATA_URL_PREFIX);

/**
 * Approximate decoded byte size of a base64 data URL, for pre-upload guards.
 * @param {string} dataUrl
 * @returns {number}
 */
export const estimateDataUrlBytes = (dataUrl) => {
  const payloadStart = dataUrl.indexOf(",") + 1;
  return Math.ceil((dataUrl.length - payloadStart) * BASE64_BYTES_PER_CHAR);
};

/**
 * Image elements still carrying inline base64 pixels that should move to
 * blob storage.
 * @param {Array<object>} elements
 * @returns {Array<{ id: string, src: string }>}
 */
export const findUploadableImageElements = (elements) =>
  elements
    .filter(
      (element) =>
        element.type === CANVAS_ELEMENT_TYPES.IMAGE && isDataUrlSrc(element.src)
    )
    .map(({ id, src }) => ({ id, src }));
